import React, { useState } from 'react';
import { ArrowRight, CheckCircle, Cloud, Brain } from 'lucide-react';

const BUTTON_BG_COLOR = "#3B82F6"; // blue-500
const BUTTON_BG_HOVER_COLOR = "#2563EB"; // blue-600
const ICON_COLOR = "#60A5FA"; // blue-400
const AI_ICON_COLOR = "#8B5CF6"; // violet-500

interface HeroSectionProps {
  onOpenContactModal: () => void;
}

const HeroSection: React.FC<HeroSectionProps> = ({ onOpenContactModal }) => {
  const [activeAssessment, setActiveAssessment] = useState<'ai' | 'cloud'>('ai');

  return (
    <div id="home" className="bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 py-12 pt-20 md:pt-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row items-center justify-between">
          <div className="md:w-1/2 mb-10 md:mb-0 md:pr-10 text-center md:text-left">
            <h1 className="text-4xl font-extrabold text-white sm:text-5xl sm:tracking-tight lg:text-6xl">
              AI Workflow Automation Experts
              <span className="text-2xl sm:text-3xl lg:text-4xl text-gray-300 block mt-2">& AWS Cloud Solutions</span>
            </h1>
            <p className="mt-5 text-xl text-gray-300">
              Automate your business processes and eliminate daily operational tasks with custom AI solutions built on reliable cloud infrastructure
            </p>
            <div className="mt-8 flex space-x-4 justify-center md:justify-start">
              <button
                onClick={() => {
                  const servicesSection = document.getElementById('services');
                  if (servicesSection) {
                    servicesSection.scrollIntoView({ behavior: 'smooth' });
                  }
                }}
                style={{ backgroundColor: BUTTON_BG_COLOR }}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = BUTTON_BG_HOVER_COLOR}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = BUTTON_BG_COLOR}
                className="text-white px-5 py-3 rounded-md text-base font-medium shadow-md transition-colors duration-150"
              >
                Our Solutions
              </button>
              <button
                onClick={onOpenContactModal}
                className="border border-gray-300 text-white hover:bg-gray-700 px-5 py-3 rounded-md text-base font-medium transition-colors duration-150">
                Contact Us
              </button>
            </div>
          </div>
          <div className="md:w-1/2 flex justify-center md:justify-end">
            <div className="w-full max-w-md bg-gray-800 rounded-lg shadow-xl border border-gray-700">
              {/* Assessment Toggle */}
              <div className="flex border-b border-gray-700">
                <button
                  onClick={() => setActiveAssessment('ai')}
                  className={`flex-1 py-3 px-4 text-sm font-medium transition-colors duration-150 ${
                    activeAssessment === 'ai'
                      ? 'bg-gray-700 text-white border-b-2 border-blue-500'
                      : 'text-gray-400 hover:text-white'
                  }`}
                >
                  AI Automation
                </button>
                <button
                  onClick={() => setActiveAssessment('cloud')}
                  className={`flex-1 py-3 px-4 text-sm font-medium transition-colors duration-150 ${
                    activeAssessment === 'cloud'
                      ? 'bg-gray-700 text-white border-b-2 border-blue-500'
                      : 'text-gray-400 hover:text-white'
                  }`}
                >
                  Cloud Infrastructure
                </button>
              </div>

              <div className="p-6 h-80 flex flex-col">
                <div className="flex-grow">
                  {activeAssessment === 'ai' ? (
                    <>
                      <div className="flex items-center mb-4">
                        <Brain style={{ color: AI_ICON_COLOR }} className="h-6 w-6" />
                        <h3 className="ml-2 text-lg font-medium text-white">AI Automation Assessment</h3>
                      </div>
                      <p className="text-gray-300 mb-6">Get a free assessment of your business processes and discover automation opportunities that can save time and reduce costs.</p>
                      <div className="space-y-3">
                        <div className="flex items-start">
                          <CheckCircle className="h-5 w-5 text-green-400 mt-0.5" />
                          <p className="ml-2 text-gray-300">Process automation analysis</p>
                        </div>
                        <div className="flex items-start">
                          <CheckCircle className="h-5 w-5 text-green-400 mt-0.5" />
                          <p className="ml-2 text-gray-300">ROI calculation & time savings</p>
                        </div>
                        <div className="flex items-start">
                          <CheckCircle className="h-5 w-5 text-green-400 mt-0.5" />
                          <p className="ml-2 text-gray-300">Custom AI solution roadmap</p>
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flex items-center mb-4">
                        <Cloud style={{ color: ICON_COLOR }} className="h-6 w-6" />
                        <h3 className="ml-2 text-lg font-medium text-white">Cloud Infrastructure Assessment</h3>
                      </div>
                      <p className="text-gray-300 mb-6">Get a free assessment of your current AWS infrastructure and discover optimization opportunities.</p>
                      <div className="space-y-3">
                        <div className="flex items-start">
                          <CheckCircle className="h-5 w-5 text-green-400 mt-0.5" />
                          <p className="ml-2 text-gray-300">Cost optimization analysis</p>
                        </div>
                        <div className="flex items-start">
                          <CheckCircle className="h-5 w-5 text-green-400 mt-0.5" />
                          <p className="ml-2 text-gray-300">Security assessment</p>
                        </div>
                        <div className="flex items-start">
                          <CheckCircle className="h-5 w-5 text-green-400 mt-0.5" />
                          <p className="ml-2 text-gray-300">Performance recommendations</p>
                        </div>
                      </div>
                    </>
                  )}
                </div>
                <a
                  href="https://calendly.com/kultio"
                  target="_blank" rel="noopener noreferrer"
                  style={{ backgroundColor: BUTTON_BG_COLOR }}
                  onMouseOver={(e) => e.currentTarget.style.backgroundColor = BUTTON_BG_HOVER_COLOR}
                  onMouseOut={(e) => e.currentTarget.style.backgroundColor = BUTTON_BG_COLOR}
                  className="mt-6 w-full text-white px-4 py-2 rounded-md text-sm font-medium flex items-center justify-center transition-colors duration-150"
                >
                  Book Your Free Assessment
                  <ArrowRight className="ml-2 h-4 w-4" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HeroSection;